import { useAtomValue } from "jotai";
import { useMemo } from "react";
import type { SectionProps } from "../../../components/Questionnaire/Section";
import { createValidator } from "../../../components/Questionnaire/validation/form";
import type { RuleSet } from "../../../components/Questionnaire/validation/form/types";
import { QuestionFilters } from "../utils/QuestionFilters";
import { useQuestionnaireActions } from "./useQuestionnaireActions";
import { validateAction as validate } from "../utils/validateAction";
import { questionnaireReducerAtom } from "../state/questionnaireReducerAtom";
import { questionnaireCombinedErrorsAtom } from "../state/questionnaireCombinedErrorsAtom";
import { useQuestionnaireInitialization } from "./useQuestionnaireInitialization";

/**
 * Main questionnaire hook. Initializes state and provides full access to state + actions.
 * Re-renders on any state change - prefer specific hooks (useAnswer, useError, etc.) when possible.
 *
 * Delegates hydration and prop-change handling to useQuestionnaireInitialization,
 * then subscribes to the full state for consumers that need everything.
 *
 * @example const { answers, errors, updateAnswer } = useQuestionnaire(sections, validations);
 * @see useQuestionnaireActions - actions only, no state subscription
 * @see useQuestionnaireInitialization - initialize without subscribing
 */
export const useQuestionnaire = (
  sections?: SectionProps[],
  validations?: readonly RuleSet[],
  config: Record<string, unknown> = {},
) => {
  useQuestionnaireInitialization(sections, validations, config);

  // eslint-disable-next-line local/no-direct-reducer-atom-subscription
  const state = useAtomValue(questionnaireReducerAtom);
  const combinedErrors = useAtomValue(questionnaireCombinedErrorsAtom);
  const actions = useQuestionnaireActions();

  const validateQuestion = useMemo(
    () => createValidator(state.validations ?? []),
    [state.validations],
  );

  // question filters
  const filter = useMemo(() => new QuestionFilters(state), [state]);

  return {
    ...state,
    ...actions,
    errors: combinedErrors,
    validateQuestion,
    validateForm: validate,
    questions: {
      show: actions.setVisible,
      hide: actions.setHidden,
      filter,
    },
  };
};
